import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Logo from "../../public/AfricFreshLogo-01.svg";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Alert from '../../components/alert-message';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';

const SignUpForm = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [formProgress, setFormProgress] = useState(0);

    const navigate = useNavigate();

    // Calculate form progress
    useEffect(() => {
        let progress = 0;
        if (firstName) progress += 16.6;
        if (lastName) progress += 16.6;
        if (email) progress += 16.6;
        if (mobile) progress += 16.6;
        if (password) progress += 16.6;
        if (confirmPassword) progress += 16.6;
        setFormProgress(progress);
    }, [firstName, lastName, email, mobile, password, confirmPassword]);

    const validate = () => {
        let tempErrors = {};
        tempErrors.firstName = firstName ? "" : "First name is required.";
        tempErrors.lastName = lastName ? "" : "Last name is required.";

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{3,}$/;
        tempErrors.email = emailPattern.test(email) ? "" : "Email is not valid.";

        tempErrors.mobile = mobile.length === 10 ? "" : "Mobile number must be 10 digits.";
        tempErrors.password = password ? "" : "Password is required.";
        tempErrors.confirmPassword = confirmPassword ? "" : "Confirm Password is required.";

        if (password && confirmPassword && password !== confirmPassword) {
            tempErrors.confirmPassword = "Passwords do not match.";
        }

        setErrors(tempErrors);
        return Object.values(tempErrors).every(x => x === "");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setLoading(true);

            try {
                const response = await axios.post(
                    'https://afric-fresh-backend.onrender.com/auth/signup',
                    {
                        firstName,
                        lastName,
                        email,
                        phoneNumber: mobile,
                        password,
                        referralCode
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        },
                    }
                );

                setLoading(false);

                // Calculate expiration time (24 hours from now)
                const expiresAt = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
                
                // Store token, expiration time, and user details
                localStorage.setItem('AficFreshtoken', response.data.token);
                localStorage.setItem('AficFreshExpiresAt', expiresAt.toISOString());
                localStorage.setItem('AficFreshuserDetails', JSON.stringify(response.data.newUser));

                // Show success toast
                toast.success(
                    <div>
                      <p>Account created successfully!</p>
                      <p className="mt-2">
                        You earned <strong>20 points</strong> for signing up!
                      </p>
                      {response.data.referralCode && (
                        <p className="mt-1">
                          Your referral code: <strong>{response.data.referralCode}</strong>
                        </p>
                      )}
                      <p className="text-sm mt-1">Share your code to earn more points!</p>
                    </div>,
                    { autoClose: 10000 }
                  );
                // Delay navigation for 3 seconds
                setTimeout(() => {
                    navigate('/');
                }, 3000);

            } catch (error) {
                setLoading(false);
                toast.error(error.response?.data?.error || 'Signup failed');
            }
        }
    };

    // Automatically clear errors after 5 seconds
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const errorTimeout = setTimeout(() => {
                setErrors({});
            }, 5000);
            return () => clearTimeout(errorTimeout);
        }
    }, [errors]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                duration: 0.5,
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.3 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: {
                type: "spring", 
                stiffness: 300,
                damping: 24
            }
        }
    };

    return (
        <motion.div 
            className='flex min-h-screen bg-gray-100 max-sm:bg-white'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>Sign Up | AfricFresh - African Grocery Marketplace</title>
                <meta 
                    name="description" 
                    content="Create an AfricFresh account to shop for authentic African groceries, fresh produce, and food products. Get 20 bonus points when you sign up!"
                />
                <meta property="og:title" content="Sign Up | AfricFresh - African Grocery Marketplace" />
                <meta property="og:description" content="Join AfricFresh to access the best African groceries and earn bonus points on signup." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://africfresh.onrender.com/signup" />
                <meta property="og:image" content={Logo} />
                <meta name="keywords" content="African groceries signup, AfricFresh account, African food marketplace, online African store, African produce delivery" />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Sign Up | AfricFresh",
                        "description": "Create an account to shop for authentic African groceries",
                        "url": "https://africfresh.onrender.com/signup",
                        "potentialAction": {
                            "@type": "RegisterAction",
                            "name": "Sign Up",
                            "target": "https://africfresh.onrender.com/auth/signup"
                        }
                    }
                    `}
                </script>
            </Helmet>

            <ToastContainer position="top-right" autoClose={3000} />

            <div className='flex justify-center items-center flex-1'>
                <motion.div 
                    className="flex-row flex-col"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <motion.div 
                        className="flex justify-center items-center"
                        variants={itemVariants}
                    >
                        <motion.img 
                            src={Logo} 
                            alt="AfriFresh-Logo" 
                            className="w-72 h-20 mb-2"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ 
                                duration: 0.5,
                                type: "spring",
                                stiffness: 260,
                                damping: 20
                            }}
                        />
                    </motion.div>
                    <motion.div 
                        className='bg-white p-3 rounded-lg shadow-lg max-sm:rounded-none max-sm:shadow-none max-w-sm w-full text-center h-full max-sm:justify-between'
                        variants={itemVariants}
                    >
                        <motion.h2 
                            className="text-2xl font-bold mb-3"
                            variants={itemVariants}
                        >
                            Sign up for an account
                        </motion.h2>
                        <motion.p 
                            className="text-gray-600 mb-4"
                            variants={itemVariants}
                        >
                            Welcome to AfricFresh, Houston's go-to destination for authentic African groceries and products! Whether you're craving traditional spices, fresh produce, or specialty items, we've got you covered.
                        </motion.p>
                        
                        {/* Form progress bar */}
                        <motion.div 
                            className="w-full h-1 bg-gray-200 rounded-full mb-4 overflow-hidden"
                            initial={{ width: 0 }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 0.8 }}
                        >
                            <motion.div 
                                className="h-full bg-green-500 rounded-full"
                                initial={{ width: "0%" }}
                                animate={{ width: `${formProgress}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </motion.div>

                        <AnimatePresence>
                            {message && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Alert type={alertType} message={message} onClose={() => setMessage('')} />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.form 
                            onSubmit={handleSubmit} 
                            noValidate
                            variants={containerVariants}
                        >
                            <motion.div 
                                className='flex justify-between max-sm:flex-col max-sm:space-y-3'
                                variants={itemVariants}
                            >
                                <motion.div 
                                    className="w-full mb-3"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                >
                                    <input
                                        type="text"
                                        placeholder="First name"
                                        className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                    <AnimatePresence>
                                        {errors.firstName && (
                                            <motion.p 
                                                className="text-red-500 text-xs mt-1 text-left"
                                                initial={{ opacity: 0, y: -5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -5 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                {errors.firstName}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                                <motion.div 
                                    className="w-full mb-3 ml-2 max-sm:ml-0"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                >
                                    <input
                                        type="text"
                                        placeholder="Last name"
                                        className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                    <AnimatePresence>
                                        {errors.lastName && (
                                            <motion.p 
                                                className="text-red-500 text-xs mt-1 text-left"
                                                initial={{ opacity: 0, y: -5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -5 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                {errors.lastName}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </motion.div>

                            <motion.div 
                                className="w-full mb-3"
                                variants={itemVariants}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <input
                                    type="email"
                                    placeholder="Email"
                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <AnimatePresence>
                                    {errors.email && (
                                        <motion.p 
                                            className="text-red-500 text-xs mt-1 text-left"
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {errors.email}
                                        </motion.p>
                                    )}
                                </AnimatePresence>
                            </motion.div>

                            <motion.div 
                                className="w-full mb-3"
                                variants={itemVariants}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <input
                                    type="tel"
                                    placeholder="Mobile number"
                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value)}
                                />
                                <AnimatePresence>
                                    {errors.mobile && (
                                        <motion.p 
                                            className="text-red-500 text-xs mt-1 text-left"
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {errors.mobile}
                                        </motion.p>
                                    )}
                                </AnimatePresence>
                            </motion.div>

                            <motion.div 
                                className="w-full mb-3 relative"
                                variants={itemVariants}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg pr-10 transition-all duration-300 focus:border-[#248526]"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <motion.button
                                    type="button"
                                    className="absolute right-2 top-2.5 text-gray-500"
                                    onClick={togglePasswordVisibility}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </motion.button>
                                <AnimatePresence>
                                    {errors.password && (
                                        <motion.p 
                                            className="text-red-500 text-xs mt-1 text-left"
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {errors.password}
                                        </motion.p>
                                    )}
                                </AnimatePresence>
                            </motion.div>

                            <motion.div 
                                className="w-full mb-5 relative"
                                variants={itemVariants}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm Password"
                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg pr-10 transition-all duration-300 focus:border-[#248526]"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <motion.button
                                    type="button"
                                    className="absolute right-2 top-2.5 text-gray-500"
                                    onClick={toggleConfirmPasswordVisibility}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                </motion.button>
                                <AnimatePresence>
                                    {errors.confirmPassword && (
                                        <motion.p 
                                            className="text-red-500 text-xs mt-1 text-left"
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {errors.confirmPassword}
                                        </motion.p>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                                
                            <motion.div 
                                className="w-full mb-3"
                                variants={itemVariants}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 0.4 }}
                            >
                                <div className="flex flex-col mb-2">
                                    <div className="flex items-center">
                                        <motion.label 
                                            htmlFor="referralCode" 
                                            className="text-sm font-medium text-gray-700"
                                            initial={{ x: -5, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            transition={{ delay: 0.4 }}
                                        >
                                            Have a referral code?
                                        </motion.label>
                                        <motion.span 
                                            className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium"
                                            initial={{ scale: 0, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ 
                                                delay: 0.5, 
                                                type: "spring",
                                                stiffness: 400,
                                                damping: 10
                                            }}
                                        >
                                            Earn bonus points
                                        </motion.span>
                                    </div>
                                </div>
                                <motion.div 
                                    className="relative"
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                >
                                    <input
                                        type="text"
                                        id="referralCode"
                                        placeholder="Enter referral code (optional)"
                                        className="w-full p-3 border border-gray-300 focus:border-green-500 outline-none rounded-lg transition-all"
                                        value={referralCode}
                                        onChange={(e) => setReferralCode(e.target.value)}
                                    />
                                    {referralCode && (
                                        <motion.button
                                            type="button"
                                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                            onClick={() => setReferralCode('')}
                                            whileHover={{ scale: 1.2 }}
                                            whileTap={{ scale: 0.8 }}
                                        >
                                            ×
                                        </motion.button>
                                    )}
                                </motion.div>
                                <motion.p 
                                    className="text-xs text-gray-500 mt-2"
                                    initial={{ y: 5, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.6 }}
                                >
                                    Ask a friend for their code or check your email if you've been referred before
                                </motion.p>
                            </motion.div>

                            <motion.div 
                                className="mt-auto"
                                variants={itemVariants}
                            >
                                <motion.button
                                    type="submit"
                                    className="bg-[#248526] text-white py-2 w-full rounded-lg hover:bg-[#144b15] flex justify-center items-center"
                                    disabled={loading}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                >
                                    {loading ? (
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                    ) : (
                                        "Create account"
                                    )}
                                </motion.button>
                            </motion.div>
                        </motion.form>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7, duration: 0.3 }}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                        >
                            <Link to="/auth/login" className="text-[#154F16] mt-5 font-bold underline mb-4 block">
                                Already have an account?
                            </Link>
                        </motion.div>

                        <motion.p 
                            className="text-xs text-gray-500 mt-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8, duration: 0.3 }}
                        >
                            By logging in, I agree to the AfricFresh{' '}
                            <a href="#" className="text-[#154F16] hover:underline">
                                Terms & Conditions
                            </a>{' '}
                            and acknowledge the{' '}
                            <a href="#" className="text-[#154F16] hover:underline">
                                Privacy Policy
                            </a>.
                        </motion.p>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default SignUpForm;